import { Injectable, signal, computed } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import {
  FeatureAttribute,
  isFeatureAttribute,
} from '@app/data/models/common/feature.model';

/**
 * Section state management service
 *
 * Manages section-related state that was previously handled by DatabaseCacheService.
 * Uses signals for reactive state management.
 */
@Injectable({ providedIn: 'root' })
export class SectionStateService {
  /** Notifies subscribers when section-related data is updated */
  readonly sectionUpdated = new Subject<void>();

  /** Currently selected section */
  private readonly _selectedSection = signal<Section | null>(null);

  /** All sections available to the current user */
  private readonly _sections = signal<Section[]>([]);

  /** Section documents from the server */
  private readonly _sectionDocuments = signal<SectionDocument[]>([]);

  /** Section group documents */
  private readonly _sectionGroupDocuments = signal<SectionGroupDocument[]>([]);

  /** Currently selected section group */
  private readonly _selectedSectionGroup = signal<SectionGroupDocument | null>(null);

  /** Sections with user roles */
  private readonly _sectionWithRole = signal<SectionWithRole[]>([]);

  /** Selected section with role information */
  private readonly _selectedSectionWithRole = signal<SectionWithRole | null>(null);

  /** Contract info for selected section */
  private readonly _selectedSectionContractInfo = signal<ContractInfo | null>(null);

  // Public read-only signals
  readonly selectedSection = this._selectedSection.asReadonly();
  readonly sections = this._sections.asReadonly();
  readonly sectionDocuments = this._sectionDocuments.asReadonly();
  readonly sectionGroupDocuments = this._sectionGroupDocuments.asReadonly();
  readonly selectedSectionGroup = this._selectedSectionGroup.asReadonly();
  readonly sectionWithRole = this._sectionWithRole.asReadonly();
  readonly selectedSectionWithRole = this._selectedSectionWithRole.asReadonly();
  readonly selectedSectionContractInfo = this._selectedSectionContractInfo.asReadonly();

  // Observable versions for compatibility with existing code
  readonly selectedSection$ = toObservable(this._selectedSection);
  readonly sections$ = toObservable(this._sections);

  // Computed values
  readonly selectedSectionId = computed(() => this._selectedSection()?.sectionId ?? null);
  readonly selectedSectionName = computed(() => this._selectedSection()?.sectionName ?? null);

  readonly selectedSectionGroupAssets = computed(() => {
    const group = this._selectedSectionGroup();
    return group?.settings?.assets ?? null;
  });

  readonly selectedSectionGroupFunctions = computed<FeatureAttribute[]>(() => {
    const group = this._selectedSectionGroup();
    const raw = group?.settings?.functionAttributes;
    if (!Array.isArray(raw)) return [];
    // 型安全なフィルタリング - 不正な値を除外
    return raw.filter(isFeatureAttribute);
  });

  readonly selectedSectionGroupUserDataAttributes = computed(() => {
    const group = this._selectedSectionGroup();
    return group?.settings?.userDataAttributes ?? null;
  });

  readonly selectedSectionGroupLogoUrl = computed(() => {
    const assets = this.selectedSectionGroupAssets();
    const logoUrl = assets?.logoUrl;

    if (!logoUrl || logoUrl === '' || logoUrl === 'http://') {
      return 'https://gokuridevitemstest.blob.core.windows.net/section-group-assets/gokuri_logo_bk.svg?sp=r&st=2023-02-13T07:43:06Z&se=3023-02-13T15:43:06Z&spr=https&sv=2021-06-08&sr=b&sig=QBi1qOR7NcuMrHhEg87%2BxmObo%2F5OD64jy3gtz027hq8%3D';
    }

    return logoUrl;
  });

  readonly selectedSectionRoles = computed(() => {
    const sectionWithRole = this._selectedSectionWithRole();
    return sectionWithRole?.permissions?.role ?? null;
  });

  readonly selectedSectionJobTitleCode = computed(() => {
    const sectionWithRole = this._selectedSectionWithRole();
    const jobTitleCode = sectionWithRole?.attributes?.jobTitleCode;
    return jobTitleCode ?? 'OTHER';
  });

  readonly selectedSectionContractEnabled = computed(() => {
    const contractInfo = this._selectedSectionContractInfo();
    if (!contractInfo) {
      return null;
    }
    return contractInfo.contractStatus !== 'CLOSE';
  });

  // Setters
  setSelectedSection(section: Section | null): void {
    this._selectedSection.set(section);
  }

  setSections(sections: Section[]): void {
    this._sections.set(sections);
  }

  setSectionDocuments(documents: SectionDocument[]): void {
    this._sectionDocuments.set(documents);
  }

  setSectionGroupDocuments(documents: SectionGroupDocument[]): void {
    this._sectionGroupDocuments.set(documents);
  }

  setSelectedSectionGroup(group: SectionGroupDocument | null): void {
    this._selectedSectionGroup.set(group);
  }

  setSelectedSectionGroupById(sectionGroupId: string): void {
    const group = this._sectionGroupDocuments().find(g => g.id === sectionGroupId) ?? null;
    this._selectedSectionGroup.set(group);
    this.sectionUpdated.next();
  }

  setSectionWithRole(sectionWithRole: SectionWithRole[]): void {
    this._sectionWithRole.set(sectionWithRole);
  }

  setSelectedSectionWithRole(sectionWithRole: SectionWithRole | null): void {
    this._selectedSectionWithRole.set(sectionWithRole);
  }

  setSelectedSectionContractInfo(contractInfo: ContractInfo | null): void {
    this._selectedSectionContractInfo.set(contractInfo);
  }

  setSelectedSectionRoles(roles: string[]): void {
    const current = this._selectedSectionWithRole();
    if (current) {
      this._selectedSectionWithRole.set({
        ...current,
        permissions: {
          ...current.permissions,
          role: roles
        }
      });
    }
  }

  // Query methods
  findSectionById(sectionId: string): Section | undefined {
    return this._sections().find(s => s.sectionId === sectionId);
  }

  findSectionDocumentById(sectionId: string): SectionDocument | undefined {
    return this._sectionDocuments().find(s => s.id === sectionId);
  }

  findSectionGroupById(sectionGroupId: string): SectionGroupDocument | undefined {
    return this._sectionGroupDocuments().find(g => g.id === sectionGroupId);
  }

  findSectionWithRoleById(sectionId: string): SectionWithRole | undefined {
    return this._sectionWithRole().find(s => s.sectionId === sectionId);
  }

  // Reset state
  reset(): void {
    this._selectedSection.set(null);
    this._sections.set([]);
    this._sectionDocuments.set([]);
    this._sectionGroupDocuments.set([]);
    this._selectedSectionGroup.set(null);
    this._sectionWithRole.set([]);
    this._selectedSectionWithRole.set(null);
    this._selectedSectionContractInfo.set(null);
  }
}

// Type definitions
export interface Section {
  sectionId: string;
  sectionName: string;
}

export interface SectionDocument {
  id: string;
  sectionGroupId: string;
  contractInfo: {
    contractStatus: string;
    contractStartAt: string;
    contractEndAt: string;
  };
  [key: string]: unknown;
}

export interface SectionGroupDocument {
  id: string;
  settings?: {
    assets?: {
      logoUrl?: string;
      [key: string]: unknown;
    };
    functionAttributes?: unknown[];
    userDataAttributes?: unknown[];
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface SectionWithRole {
  sectionId: string;
  sectionName: string;
  permissions: {
    role: string[];
    [key: string]: unknown;
  };
  attributes?: {
    jobTitleCode?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface ContractInfo {
  contractStatus: string;
  contractStartAt: string;
  contractEndAt: string;
}
